import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, Clock, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

const next14 = () => {
  const days = [];
  const now = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    days.push(d);
  }
  return days;
};

function stepCircleClass(step, index) {
  if (step > index + 1) return "zenith-cta text-white";
  if (step === index + 1) return "bg-primary-fixed text-primary";
  return "bg-surface-container text-outline";
}

function confirmLabel(busy, type) {
  if (busy) return "Processing…";
  if (type?.price > 0) return `Pay $${type.price} & confirm`;
  return "Confirm booking";
}

export default function Booking() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [days] = useState(next14());
  const [selectedDay, setSelectedDay] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/session-types").then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const loadSlots = async (d) => {
    setSelectedDay(d);
    setSelectedSlot(null);
    setLoadingSlots(true);
    try {
      const r = await api.get("/booking/slots", { params: { date: dateKey(d), timezone_str: tz } });
      setSlots(r.data.slots || []);
    } catch (e) {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await api.post("/booking/reserve", { sessionType: selectedType.name, startUtc: selectedSlot.startUtc });
      const reservationId = res.data.reservationId;
      if (selectedType.price > 0) {
        const co = await api.post("/payments/checkout", { reservationId, origin_url: window.location.origin });
        window.location.href = co.data.checkout_url;
      } else {
        await api.post("/booking/confirm", { reservationId });
        toast.success("Session booked! Check your notifications.");
        navigate("/app/bookings");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete booking");
      setBusy(false);
    }
  };

  const steps = ["Session", "Time", "Confirm"];

  const renderTimes = () => {
    if (loadingSlots) {
      return <p className="text-on-surface-variant text-sm py-6">Finding open slots…</p>;
    }
    if (slots.length === 0) {
      return (
        <div className="bg-surface-container-low rounded-3xl p-8 text-center" data-testid="no-slots">
          <p className="text-on-surface-variant">No openings this day. Try another date.</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {slots.map((s) => (
          <button
            key={s.startUtc}
            onClick={() => setSelectedSlot(s)}
            className={`min-h-[48px] rounded-2xl py-3 text-sm font-medium transition-colors duration-300 ${selectedSlot?.startUtc === s.startUtc ? "zenith-cta text-white" : "bg-surface-container-lowest text-primary hover:bg-primary-fixed"}`}
            data-testid="time-slot"
          >
            {s.localTime}
          </button>
        ))}
      </div>
    );
  };

  return (
    <AppShell active="book">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-headline font-light text-primary tracking-tight">Book a session</h1>
        <p className="mt-3 text-on-surface-variant">Three gentle steps. Under a minute.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-10">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors duration-300 ${stepCircleClass(step, i)}`}>
              {step > i + 1 ? <Check className="w-4 h-4" strokeWidth={2} /> : i + 1}
            </div>
            <span className={`text-sm hidden sm:block ${step >= i + 1 ? "text-primary" : "text-outline"}`}>{s}</span>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-outline-variant/40" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4 animate-rise-in">
          {types.map((t) => (
            <button
              key={t.name}
              onClick={() => { setSelectedType(t); setStep(2); }}
              className="w-full text-left bg-surface-container-lowest rounded-3xl p-6 shadow-ambient hover:bg-primary-fixed transition-colors duration-300 flex items-center justify-between"
              data-testid="session-type-option"
            >
              <div>
                <p className="font-headline text-xl text-primary">{t.name}</p>
                <p className="text-sm text-on-surface-variant mt-1 max-w-md">{t.description}</p>
                <p className="text-xs text-outline mt-2 flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.5} /> {t.durationMinutes} min</p>
              </div>
              <span className="font-headline text-2xl text-primary shrink-0 ml-4">{t.price > 0 ? `$${t.price}` : "Free"}</span>
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="animate-rise-in">
          <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-on-surface-variant mb-5 hover:text-primary transition-colors duration-300" data-testid="booking-back">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> {selectedType?.name}
          </button>

          <p className="text-xs uppercase tracking-widest text-outline mb-3">Pick a day</p>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 mb-8">
            {days.map((d) => {
              const isSel = selectedDay && dateKey(d) === dateKey(selectedDay);
              return (
                <button
                  key={d.toISOString()}
                  onClick={() => loadSlots(d)}
                  className={`shrink-0 w-16 h-20 rounded-3xl flex flex-col items-center justify-center transition-colors duration-300 ${isSel ? "zenith-cta text-white" : "bg-surface-container-lowest text-on-surface hover:bg-primary-fixed"}`}
                  data-testid="booking-day"
                >
                  <span className="text-[11px] uppercase opacity-70">{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
                  <span className="font-headline text-2xl">{d.getDate()}</span>
                </button>
              );
            })}
          </div>

          {selectedDay && (
            <>
              <p className="text-xs uppercase tracking-widest text-outline mb-3">Available times ({tz.replace("_", " ")})</p>
              {renderTimes()}
            </>
          )}

          {selectedSlot && (
            <button onClick={() => setStep(3)} className="mt-8 w-full py-4 rounded-2xl zenith-cta text-white font-medium hover:opacity-90 transition-opacity duration-300" data-testid="booking-continue">
              Continue
            </button>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="animate-rise-in">
          <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-on-surface-variant mb-5 hover:text-primary transition-colors duration-300" data-testid="booking-back-2">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Change time
          </button>

          <div className="bg-surface-container-lowest rounded-3xl p-8 shadow-ambient" data-testid="booking-summary">
            <p className="text-xs uppercase tracking-widest text-outline">Confirm your booking</p>
            <p className="font-headline text-3xl text-primary mt-3">{selectedType?.name}</p>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-on-surface-variant">When</span><span className="text-on-surface font-medium">{new Date(selectedSlot.startUtc).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</span></div>
              <div className="flex justify-between"><span className="text-on-surface-variant">Duration</span><span className="text-on-surface font-medium">{selectedType?.durationMinutes} min</span></div>
              <div className="flex justify-between"><span className="text-on-surface-variant">Coach</span><span className="text-on-surface font-medium">Chris Holliday</span></div>
              <div className="flex justify-between pt-3"><span className="text-on-surface-variant">Total</span><span className="font-headline text-xl text-primary">{selectedType?.price > 0 ? `$${selectedType.price}` : "Free"}</span></div>
            </div>
          </div>

          <p className="text-xs text-outline mt-4 text-center">You'll get an instant WhatsApp & email confirmation with a calendar invite.</p>

          <button onClick={confirm} disabled={busy} className="mt-6 w-full flex items-center justify-center gap-2 py-4 rounded-2xl zenith-cta text-white font-medium hover:opacity-90 transition-opacity duration-300 disabled:opacity-50" data-testid="booking-confirm-button">
            <CalendarPlus className="w-4 h-4" strokeWidth={1.5} />
            {confirmLabel(busy, selectedType)}
          </button>
        </div>
      )}
    </AppShell>
  );
}
